'use client';

import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { useIsMobile } from '@/hooks/use-mobile';
import { FvdPopover } from './FvdPopover';
import { useOffReason } from './FvdOffReason';
import { FvdSegmented } from './FvdSegmented';
import { SELECTED_BORDER } from './selected';

/**
 * The buttons. ONE definition, quoted from the style guide.
 *
 * `/design-tokens` is the style guide, and its "Controls in context" section is
 * the spec: 33px tall, 13px text at weight 500, 8px corner, teal fill with no
 * border for primary and a hairline for secondary. Light and quiet, not chunky.
 *
 * This file exists because the Photos preview did not do that. It grew its own
 * button at 44px, 13.5px and weight 600, which reads as the OLD product next to
 * everything else in the redesign, and the difference is obvious the moment the
 * two sit on one screen. Buttons are exactly the kind of thing that drifts when
 * each screen styles its own, so no screen styles its own any more: FvdTable and
 * every preview import from here.
 *
 * Touch is handled per button rather than by a taller button everywhere: 33px is
 * right with a mouse, and the 44px floor comes back on a phone. That floor is
 * applied with `useIsMobile()` and NOT with a class, because a rule written for
 * `.fvd-btn` in globals.css was stripped from the compiled stylesheet by
 * Tailwind v4 while the rule beside it survived. CLAUDE.md prescribes the hook
 * for exactly this reason, and a button that silently loses its tap target is
 * not something to leave to a scanner's opinion.
 *
 * Delete with /design-tokens once the redesign is finished.
 */

export const fvdButtonPrimary: CSSProperties = {
  height: 33,
  padding: '0 14px',
  borderRadius: 'var(--fvd-radius)',
  border: 'none',
  background: 'var(--fvd-teal-solid, var(--fvd-teal))',
  color: 'var(--fvd-teal-solid-on, var(--fvd-teal-on))',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const fvdButtonSecondary: CSSProperties = {
  height: 33,
  padding: '0 14px',
  borderRadius: 'var(--fvd-radius)',
  border: '1px solid var(--fvd-border-control)',
  background: 'transparent',
  color: 'var(--fvd-ink)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const fvdButtonSecondarySmall: CSSProperties = {
  ...fvdButtonSecondary,
  height: 29,
  padding: '0 11px',
  fontSize: 12.5,
  borderRadius: 'var(--fvd-radius-sm)',
};

/**
 * Destructive. The guide has no danger button, because the screens it drew have
 * nothing destructive on them. It is the secondary shape in the danger ink
 * rather than a filled red block: a filled destructive button pulls the eye to
 * the one thing on the screen you least want pressed by accident.
 */
export const fvdButtonDanger: CSSProperties = {
  ...fvdButtonSecondary,
  border: '1px solid var(--fvd-danger)',
  color: 'var(--fvd-danger)',
};

/**
 * A text link or a text action ("Add an end time", "Clear", "choose a file").
 *
 * Ink with a soft underline, not teal. Teal is kept for the few things that
 * mean "on" or "go" (see fvdChip), and a screen of teal links read as a screen
 * of highlights. The underline is what says "you can click this"; it darkens
 * to the ink on hover where the element also carries `className="fvd-link"`.
 * An <a> uses the class alone: `<a className="fvd-link" href=…>`.
 */
export const fvdLinkButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 12.5,
  fontWeight: 500,
  color: 'var(--fvd-ink)',
  textDecoration: 'underline',
  textDecorationColor: 'var(--fvd-border-control)',
  textDecorationThickness: 1,
  textUnderlineOffset: 3,
  cursor: 'pointer',
};

export const fvdFilterChip: CSSProperties = {
  height: 29,
  padding: '0 11px',
  borderRadius: 'var(--fvd-radius-pill)',
  border: '1px dashed var(--fvd-border-control)',
  background: 'transparent',
  color: 'var(--fvd-ink-secondary)',
  fontSize: 12.5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/**
 * A selectable chip: album names, token inserts, anything picked from a row of
 * options. The 8px corner is the guide's, not a pill: pills are for a filter
 * you add and remove, and these are a segmented choice.
 *
 * Chosen is a thin outline in the brand's dark green with no fill
 * (SELECTED_BORDER, see ./selected), not ink (CEO, 24 Oct) and not the bright
 * teal: a wall of filled or bright green made Setup read as all highlight, and
 * the primary stopped standing out.
 *
 * This is the pick-one chip. A chip in a row where several can be on (or a
 * setting that is on or off by itself) is FvdMultiChip, which adds a teal
 * tick. A button that shows or hides a panel is neither, and stays fvdChip.
 */
export function fvdChip(on: boolean): CSSProperties {
  return {
    height: 29,
    padding: '0 12px',
    borderRadius: 'var(--fvd-radius-sm)',
    border: `1px solid ${on ? SELECTED_BORDER : 'var(--fvd-border-control)'}`,
    background: 'transparent',
    color: on ? 'var(--fvd-ink)' : 'var(--fvd-ink-secondary)',
    fontSize: 12.5,
    fontWeight: on ? 550 : 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

/**
 * An option card (one of several to pick, each a title and at most one short
 * line). Chosen: the dark green outline (SELECTED_BORDER), no fill. The
 * only other teal on it is its FvdChoiceMark.
 * Spread it, then add the layout: `{ ...fvdChoiceCard(on), padding: '12px 14px' }`.
 */
export function fvdChoiceCard(on: boolean): CSSProperties {
  return {
    border: `1px solid ${on ? SELECTED_BORDER : 'var(--fvd-hairline-strong)'}`,
    background: 'transparent',
    borderRadius: 'var(--fvd-radius-panel)',
    color: 'var(--fvd-ink)',
    transition: 'border-color 120ms ease',
  };
}

/**
 * The tick (several can be picked) or the radio dot (one of them) on an option
 * card: the one teal thing on a chosen card.
 */
export function FvdChoiceMark({ on, kind = 'check' }: { on: boolean; kind?: 'check' | 'radio' }) {
  if (kind === 'radio') {
    return (
      <span aria-hidden style={{
        flex: 'none', width: 18, height: 18, marginTop: 1, borderRadius: '50%', boxSizing: 'border-box',
        border: `1.5px solid ${on ? 'var(--fvd-mark-fill, var(--fvd-teal))' : 'var(--fvd-border-control)'}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {on && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fvd-mark-fill, var(--fvd-teal))' }} />}
      </span>
    );
  }
  return (
    <span aria-hidden style={{
      flex: 'none', width: 18, height: 18, marginTop: 1, borderRadius: 5, boxSizing: 'border-box',
      // --fvd-mark-*: an event page's skin (PhotoPageSkin) fills it as its ticket page does.
      border: on ? 'none' : '1.5px solid var(--fvd-border-control)', background: on ? 'var(--fvd-mark-fill, var(--fvd-teal))' : 'transparent',
      color: 'var(--fvd-mark-on, var(--fvd-teal-on))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {on && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
      )}
    </span>
  );
}

/** The tick's size on a chip, in px. */
export const FVD_CHIP_TICK_SIZE = 11;

/**
 * The tick on a chip that is one of several you can turn on: a multi-pick row
 * (divisions, days, roles, sizes), or a setting that is on or off by itself.
 *
 * On a pick-one pair the outline alone says which is chosen. On a row where
 * several can be on, the dark outline read as black and did not say "this one
 * is on" (CEO, 2 Nov). So a multi-pick chip that is on carries this tick, the
 * FvdChoiceMark tick drawn bare in the same teal: the chip's one teal thing.
 * No fill and no bright outline (a wall of green, 24 Oct). Pick-one chips do
 * not get it.
 *
 * The slot grows in from nothing as the chip turns on, so a chip that is off
 * keeps even padding and one that turns on widens on purpose rather than
 * jumping. `gap` is the flex gap of the chip it sits in: while off, the slot
 * gives that gap back, so nothing is left in front of the label.
 *
 * For a whole chip use FvdMultiChip. This is for a chip drawn by hand (a pill
 * in a filter, a shared chip with its own layout).
 */
export function FvdChipTick({ on, gap = 0 }: { on: boolean; gap?: number }) {
  return (
    <span
      aria-hidden
      className="fvd-chip-tick"
      data-fvd-chip-tick={on ? 'on' : 'off'}
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        width: on ? FVD_CHIP_TICK_SIZE : 0,
        marginRight: on ? 0 : -gap,
        overflow: 'hidden',
        opacity: on ? 1 : 0,
        // --fvd-mark-*: as FvdChoiceMark, so an event page's skin recolours both.
        color: 'var(--fvd-mark-fill, var(--fvd-teal))',
        transition: 'width 140ms ease, margin-right 140ms ease, opacity 140ms ease',
      }}
    >
      <svg width={FVD_CHIP_TICK_SIZE} height={FVD_CHIP_TICK_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );
}

/**
 * A multi-pick chip: fvdChip with the tick (FvdChipTick) in front of its
 * label when on, and aria-pressed (or aria-checked, when given
 * role="checkbox"). Use it for every chip whose click turns one of several on
 * or off; a chip that picks one value stays a plain fvdChip button.
 *
 *   <FvdMultiChip on={on} onClick={() => toggle(id)} style={{ minHeight: isMobile ? 44 : undefined }}>
 *     {name}
 *   </FvdMultiChip>
 *
 * It lays its content out in a row with `gap` between items (a colour dot and
 * a name, say), so the caller's style should not set display or gap.
 */
export function FvdMultiChip({
  on,
  gap = 6,
  style,
  children,
  type = 'button',
  role,
  ...rest
}: { on: boolean; gap?: number } & ButtonHTMLAttributes<HTMLButtonElement>) {
  // A toggle button says aria-pressed; a chip given role="checkbox" says aria-checked.
  const state = role === 'checkbox' ? { 'aria-checked': on } : { 'aria-pressed': on };
  return (
    <button
      type={type}
      role={role}
      {...state}
      {...rest}
      style={{ ...fvdChip(on), ...style, display: 'inline-flex', alignItems: 'center', gap }}
    >
      <FvdChipTick on={on} gap={gap} />
      {children}
    </button>
  );
}

/**
 * A checkbox, in v8: the kit's tick (FvdChoiceMark) over a real, visually
 * hidden <input type="checkbox">, so it is still a checkbox to a keyboard and
 * a screen reader, with the kit's focus ring on the box. Use it wherever a
 * native checkbox would be drawn (the OS box ignores the theme, and is white
 * in dark mode). The label is the click target too; on a phone the row is
 * 44px tall.
 *
 *   <FvdCheckbox checked={x} onChange={setX}>Email me a copy</FvdCheckbox>
 *   <FvdCheckbox checked={x} onChange={setX} label="Select Aoife" />  (no visible text)
 */
export function FvdCheckbox({
  checked,
  onChange,
  children,
  label,
  note,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The visible label. */
  children?: ReactNode;
  /** Read out instead, when there is no visible label. */
  label?: string;
  /** One short line under the label. */
  note?: ReactNode;
  disabled?: boolean;
}) {
  const isMobile = useIsMobile();
  return (
    <label
      className="fvd-checkbox"
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 9,
        minHeight: isMobile && children ? 44 : undefined,
        paddingBlock: isMobile && children ? 11 : 0,
        boxSizing: 'border-box',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        color: 'var(--fvd-ink)',
        fontSize: 13.5,
        lineHeight: 1.45,
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={children ? undefined : label}
          onChange={(e) => onChange(e.target.checked)}
          style={{ position: 'absolute', inset: 0, margin: 0, opacity: 0, width: '100%', height: '100%', cursor: 'inherit' }}
        />
        <FvdChoiceMark on={checked} />
      </span>
      {children && (
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block' }}>{children}</span>
          {note && <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--fvd-ink-muted)', lineHeight: 1.5 }}>{note}</span>}
        </span>
      )}
    </label>
  );
}

export type FvdButtonVariant = 'primary' | 'secondary' | 'danger' | 'small';

const VARIANTS: Record<FvdButtonVariant, CSSProperties> = {
  primary: fvdButtonPrimary,
  secondary: fvdButtonSecondary,
  danger: fvdButtonDanger,
  small: fvdButtonSecondarySmall,
};

export function FvdButton({
  children,
  onClick,
  variant = 'secondary',
  disabled,
  full,
  ariaLabel,
  type = 'button',
  busy,
  ariaExpanded,
  why,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: FvdButtonVariant;
  disabled?: boolean;
  /**
   * Off, and why: the button stays focusable (aria-disabled), looks off, and
   * shows this on hover, focus and a tap, never running onClick
   * (FvdOffReason). For a reason the organiser can act on; `disabled` is for
   * a moment that passes on its own.
   */
  why?: string | null;
  full?: boolean;
  ariaLabel?: string;
  /** For a button that shows and hides something (a disclosure). */
  ariaExpanded?: boolean;
  /** 'button' unless asked: inside a form an untyped button submits it. */
  type?: 'button' | 'submit';
  /**
   * Working on it (a save in flight). Not the same as disabled: the button
   * keeps its colour, so it reads as "this is happening" rather than "you
   * can't", ignores further clicks, and tells a screen reader it is busy.
   * The caller draws the spinner and the "Saving" label (FvdSetupSave does).
   */
  busy?: boolean;
}) {
  const isMobile = useIsMobile();
  const reason = useOffReason(disabled ? null : why);
  const looksOff = disabled || reason.off;
  const inert = disabled || busy || reason.off;

  return (
    <>
    <button
      type={type}
      // Kept as a handle for QA and for anything that needs to find every
      // button on a screen. It carries no styling: see the note above.
      // Hover comes from the shared hover classes, which do survive the
      // build (the .fvd-btn rule did not, which is why these had none):
      // a tint on outlined buttons, a darken on the solid primary.
      className={`fvd-btn${reason.off ? ' fvd-offbtn' : inert ? '' : variant === 'primary' ? ' fvd-hoverable-solid' : ' fvd-hoverable'}`}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-busy={busy || undefined}
      onClick={reason.off ? (e) => reason.press(e) : inert ? undefined : onClick}
      // Busy stays focusable (aria-disabled, not disabled), so focus is not
      // thrown back to the page while the save runs. So does off with a why.
      disabled={disabled}
      aria-disabled={busy || undefined}
      {...reason.controlProps}
      style={{
        ...VARIANTS[variant],
        // The tap target, on a phone only.
        minHeight: isMobile ? 44 : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        // A primary that cannot be used yet is grey, not faded teal, as
        // FvdWindow draws it: faded, the HYROX skin put yellow text on grey.
        ...(looksOff && variant === 'primary'
          ? { background: 'var(--fvd-neutral-chip)', color: 'var(--fvd-ink-disabled)', opacity: 1 }
          : { opacity: looksOff ? 0.5 : 1 }),
        cursor: looksOff ? 'default' : busy ? 'progress' : 'pointer',
        width: full ? '100%' : undefined,
      }}
    >
      {children}
    </button>
    {reason.node}
    </>
  );
}

/**
 * The switch.
 *
 * The style guide does not draw one: the screens it drew have nothing to switch
 * on and off. So the first version here was the OLD product's switch, copied
 * over, and it looked it: a fat pill with a big white ball, filled grey when
 * off. Beside a 33px hairline button that reads as a control from a different
 * product.
 *
 * Redrawn in this system's language instead. Off is transparent with a hairline
 * (the way the secondary button is drawn) rather than a grey slab, on is a teal
 * fill, and the handle is a small dot with real space around it rather than a
 * ball filling the track. Finer, quieter, and it sits at the same visual weight
 * as everything beside it.
 *
 * `role="switch"` earns its keep twice: it is what a screen reader needs, and
 * globals.css exempts it from the 44px tap-target net, which would otherwise
 * stretch the track and strand the absolutely-positioned handle. Do not add a
 * min-height here.
 */
export function FvdSwitch({
  on,
  onChange,
  label,
  disabled,
  id,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** Can't be changed right now (saving, or not allowed): faded, still readable. */
  disabled?: boolean;
  /** For a <label htmlFor> beside it, so tapping the words toggles it too. */
  id?: string;
}) {
  return (
    <button
      id={id}
      // Never a submit: inside a form an untyped button saves the form.
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : () => onChange(!on)}
      style={{
        position: 'relative',
        flex: 'none',
        boxSizing: 'border-box',
        width: 34,
        height: 20,
        padding: 0,
        borderRadius: 'var(--fvd-radius-pill)',
        border: 'none',
        // A filled grey track when off, not an outline: the outline read as a
        // gap in the layout rather than a control you could turn on.
        background: on ? 'var(--fvd-teal)' : 'var(--fvd-switch-off)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 140ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: 'var(--fvd-switch-thumb)',
          boxShadow: 'var(--fvd-switch-thumb-shadow)',
          transition: 'left 140ms ease',
        }}
      />
    </button>
  );
}

/**
 * The old name for the pill toggle, as a tab list.
 *
 * Tabs for whole sections are FvdTabBar (the underline bar); a small switch in
 * place is FvdSegmented (the pill). This draws through FvdSegmented with tab
 * semantics, so anything still calling it looks like every other toggle. New
 * code uses one of those two directly.
 *
 * @deprecated FvdTabBar for sections, FvdSegmented for a switch.
 */
export function FvdTabs({
  tabs,
  active,
  onChange,
  label,
  style,
}: {
  tabs: Array<{ key: string; label: string; count?: number }>;
  active: string;
  onChange: (key: string) => void;
  label: string;
  style?: CSSProperties;
}) {
  return (
    <FvdSegmented
      semantics="tabs"
      fill={false}
      label={label}
      value={active}
      onChange={onChange}
      // Counts here were always amber while the tab was not chosen.
      options={tabs.map((t) => ({ value: t.key, label: t.label, count: t.count, attention: true }))}
      style={style}
    />
  );
}

/**
 * A select.
 *
 * The previews were using `FvSelect`, which is the live product's and is built
 * entirely on the OLD token set: `--fv-border`, `--fv-hover-strong`,
 * `--fv-accent`. It renders as a filled grey block with 9px padding, so in a
 * row of 29px hairline controls it is the one thing that still looks like the
 * old dashboard. Right component, wrong design system.
 *
 * This is the same control in the redesign's language, and it keeps the two
 * properties that matter: it is never a native `<select>`, whose popup the OS
 * draws and which ignores the design system completely, and the menu is a real
 * list you can drive from the keyboard.
 */
export function FvdSelect({
  value,
  onChange,
  options,
  placeholder,
  label,
  full,
  icon,
  field,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Shown, not changeable: the value stays legible. */
  disabled?: boolean;
  /** `fontFamily`: the option drawn in that face (a font picker shows what each font looks like). */
  options: Array<{ value: string; label: string; fontFamily?: string }>;
  placeholder?: string;
  label: string;
  full?: boolean;
  /**
   * Sized as a form field, to sit in a column of FvdInputs: their padding and
   * type, so a dropdown under a text field is the same height (it was 33px
   * beside a 41px field), and 16px with a 44px floor on a phone.
   */
  field?: boolean;
  /** Drawn before the value, like the sort arrows on the Photos toolbar. */
  icon?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);
  const isMobile = useIsMobile();
  // Closing (outside click, Escape) is FvdPopover's; Escape closes only the
  // list, not a dialog round it.
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  // Escape (FvdPopover's) and picking both hand focus back to the trigger,
  // so the keyboard is never left on a list that has gone.
  const close = useCallback(() => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  }, []);

  // Opened from the keyboard or the mouse, focus lands on the chosen option
  // (or the first), so the arrows work straight away.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      const box = list.current;
      if (!box) return;
      const el = box.querySelector<HTMLElement>('[aria-selected="true"]') ?? box.querySelector<HTMLElement>('[role="option"]');
      el?.focus({ preventScroll: true });
      el?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const onListKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]'));
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'ArrowDown' ? Math.min(at + 1, items.length - 1)
        : e.key === 'ArrowUp' ? Math.max(at - 1, 0)
          : e.key === 'Home' ? 0
            : e.key === 'End' ? items.length - 1
              : null;
    if (e.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    items[next]?.focus({ preventScroll: true });
    items[next]?.scrollIntoView({ block: 'nearest' });
  };

  return (
    <div ref={wrap} style={{ position: 'relative', width: full ? '100%' : undefined }}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        className={disabled ? undefined : 'fvd-hoverable'}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        style={{
          ...fvdButtonSecondary,
          ...(field
            ? { height: 'auto', minHeight: isMobile ? 44 : 36, padding: '10px 12px', fontSize: isMobile ? 16 : 13, fontWeight: 400, lineHeight: 1.5 }
            // A phone gets the 44px tap target either way, level with the fields beside it.
            : isMobile ? { height: 44 } : {}),
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          color: disabled ? 'var(--fvd-ink-disabled)' : selected ? 'var(--fvd-ink)' : 'var(--fvd-ink-muted)',
          background: disabled ? 'var(--fvd-neutral-chip)' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {icon && <span aria-hidden style={{ display: 'inline-flex', flex: 'none' }}>{icon}</span>}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(selected?.fontFamily && { fontFamily: selected.fontFamily }) }}>
            {selected?.label ?? placeholder ?? ''}
          </span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={1.8}
          style={{
            flex: 'none',
            color: 'var(--fvd-ink-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 140ms ease',
          }}
        />
      </button>

      <FvdPopover
        anchor={wrap}
        open={open}
        onClose={close}
        role="listbox"
        label={label}
        height={Math.min(280, options.length * (isMobile ? 44 : 33) + 10)}
        // The elevated surface, so the list lifts off the page in dark mode.
        style={{ background: 'var(--fvd-elevated, var(--fvd-canvas))', maxHeight: 280, overflowY: 'auto' }}
      >
        <div ref={list} onKeyDown={onListKey}>
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={on}
              // The shared menu hover: a tint that also shows over the
              // selected option's fill, and a focus ring for the keyboard.
              className="fvd-menu-item"
              tabIndex={-1}
              onClick={() => {
                onChange(o.value);
                close();
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                fontFamily: o.fontFamily ?? 'inherit',
                // A face shown as itself reads at a size its letters can be told apart.
                fontSize: o.fontFamily ? 15 : 13,
                // A finger-sized row on a phone, as the trigger is.
                padding: isMobile ? '12px 10px' : '7px 10px',
                borderRadius: 5,
                cursor: 'pointer',
                // On one line, cut short rather than run off a phone's edge.
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                background: on ? 'var(--fvd-selected-fill)' : 'transparent',
                color: 'var(--fvd-ink)',
                fontWeight: on ? 550 : 400,
              }}
            >
              {o.label}
            </button>
          );
        })}
        </div>
      </FvdPopover>
    </div>
  );
}
